import React, {
    ReactNode, useCallback, useMemo, useState,
} from "react";
import { RiAddLine, RiLinkUnlinkM } from "@remixicon/react";
import {
    FieldTypedValue, FormField,
    FormFieldType,
    FormType,
    GetWorkflowServiceDocument,
    GetWorkflowServicesDocument,
    Integration,
    IntegrationActionType,
    useAttachWorkflowMutation,
    useGetWorkflowServiceQuery,
    useSaveFormMutation,
    useSaveIntegrationSettingsMutation,
    useSaveWorkflowMutation,
    useUninstallWorkflowServiceMutation,
    WorkflowFormField,
    WorkflowFormFieldSchema,
    WorkflowServiceOption,
    WorkflowStepKeyValueParameterInput,
    WorkflowTriggerType,
} from "../../../../__generated__/api";
import FieldTypeValueDisplay from "../../../../components/form-field/FieldTypeValueDisplay";
import { HtmlRenderer } from "../../../../components/html-render/HtmlRenderer";
import FormGroup from "../../../../components/theme/form-group/FormGroup";
import SettingsContainer from "../../../../components/theme/settings";
import Toggle from "../../../../components/theme/toggle/Toggle";
import { useSetTitle } from "../base";

import { cloneWithoutTypename } from "../../../../api";
import { useFormBuilder } from "../../../../components/dirty-tracker/FormTracker";
import { isRecord, isMap, isFormField } from "../../../../components/editable-text/JsonBuilder";
import Dropdown from "../../../../components/theme/dropdown/Dropdown";
import UserGroups, { EVERYONE_GROUP_ID } from "../../../../components/pickers/user-groups/UserGroups";
import createTableContainer from "../../../../components/theme/table";
import { ContainsId } from "../../../../components/theme/table/base";
import useDeleteConfirm from "../../../../components/confirm/Delete";
import Button from "../../../../components/theme/button/Button";
import { LoggingHandler } from "../../../../contexts/logging/LoggingContext";
import useRouting from "../../../../contexts/Routing";
import ViewAutomation from "../request-form/ViewAutomation";
import { AutomationTab } from "../../../../components/workflow-v3/providers/WorkflowModalProvider";
import { createUserWorkflowFields } from "../../../../components/workflow/WorkflowFields";
import { ReplacementGroup } from "../../../../components/replacement-group";

interface WorkflowStepKeyValueParameterInputWithId extends WorkflowStepKeyValueParameterInput, ContainsId {
    field: WorkflowFormField
}

const TableContainer = createTableContainer<WorkflowStepKeyValueParameterInputWithId>();

function copySetting(value: FieldTypedValue): FieldTypedValue {
    const copy = { ...value };
    delete copy.__typename;
    return copy;
}

const FORM_NAME = "System";
const WORKFLOW_NAME = "New System Automation";
const WORKFLOW_DESCRIPTION = "Triggered automatically when certain actions occur in the system";

const PERMISSION_GROUPS: Record<string, { text: string; description: string }> = {
    [IntegrationActionType.Execute]: {
        text: "Execute actions",
        description: "This integration allows users to execute certain actions from the ticket screen. "
            + "This controls which users are able to access this integration.",
    },
    [IntegrationActionType.UserDataProvider]: {
        text: "View User data",
        description: "This integration shows information about the user in the left hand ticket side. "
            + "This setting controls which users have access to view user information through this integration.",
    },
};

function SettingInput({
    field,
    value,
    optionValues,
    integration,
    onChange,
    option,
    onOpenWorkflow,
}: {
    field: FormField;
    value: FieldTypedValue;
    optionValues: FieldTypedValue[];
    integration: Integration;
    option: WorkflowServiceOption;
    onChange: (value: FieldTypedValue) => void;
    onOpenWorkflow: (input: WorkflowStepKeyValueParameterInputWithId) => void;
}) {
    const [saveWorkflow] = useSaveWorkflowMutation();
    const [saveForm] = useSaveFormMutation();
    const [attachWorkflow] = useAttachWorkflowMutation();

    let control: ReactNode;
    let isFullWidth = false;

    const description = option.instructions || field.description;

    const onAddItem = useCallback(async (
        newType: FormFieldType,
        list: WorkflowStepKeyValueParameterInput[],
        fieldId?: string,
    ) => {
        let newWorkflowId: string | undefined;
        if (newType === FormFieldType.Workflow || newType === FormFieldType.WorkflowTicket) {
            const newWorkflow = await saveWorkflow({
                variables: {
                    workflow: {
                        name: WORKFLOW_NAME,
                        description: "",
                        steps: [],
                    },
                },
            });

            newWorkflowId = newWorkflow.data?.saveWorkflow?.id;

            const saved = await saveForm({
                variables: {
                    form: {
                        name: FORM_NAME,
                        type: FormType.Action,
                        description: WORKFLOW_DESCRIPTION,
                        fields: [],
                        workflowId: newWorkflowId,
                    },
                },
            });

            const formId = saved.data?.saveForm?.id;
            const formName = saved.data?.saveForm?.name ?? "";

            await attachWorkflow({
                variables: {
                    id: newWorkflowId,
                    triggers: [{
                        referenceId: formId,
                        text: formName,
                        type: WorkflowTriggerType.WorkspaceAction,
                    }],
                },
            });
        }

        const newItem: WorkflowStepKeyValueParameterInput = {
            key: fieldId || `Untitled_${list.length + 1}`,
            value: {
                fieldId: "urn:fq:form-field:none",
                type: newType,
                string: newWorkflowId,
            },
        } as WorkflowStepKeyValueParameterInput;
        const updated = [...list, newItem];
        onChange?.({ ...value, type: FormFieldType.Json, string: JSON.stringify(updated) });
    }, [onChange, value]);

    const createMenuItems = useCallback((jsonValue?: WorkflowStepKeyValueParameterInput[], schema?: WorkflowFormFieldSchema) => {
        const list = jsonValue ?? [];

        let menuItems: ReactNode;

        if (isMap(schema?.root)) {
            const defaultType = (isFormField(schema.root.values)
                ? (schema.root.values.type as FormFieldType)
                : FormFieldType.Text);
            menuItems = (
                <Dropdown.Item onClick={() => onAddItem(defaultType, list)}>
                    Add
                    {" "}
                    {schema?.root.label}
                </Dropdown.Item>
            );
        } else if (isRecord(schema?.root)) {
            menuItems = schema.root.fields.map(fieldOption => (
                <Dropdown.Item
                    key={fieldOption.id}
                    onClick={() => onAddItem(fieldOption.type as FormFieldType, list, fieldOption.id)}
                >
                    Add
                    {" "}
                    {fieldOption.name}
                </Dropdown.Item>
            ));
        } else {
            menuItems = (
                <>
                    <Dropdown.Item onClick={() => onAddItem(FormFieldType.Text, list)}>Text</Dropdown.Item>
                    <Dropdown.Item onClick={() => onAddItem(FormFieldType.LongText, list)}>Long Text</Dropdown.Item>
                    <Dropdown.Item onClick={() => onAddItem(FormFieldType.Number, list)}>Number</Dropdown.Item>
                    <Dropdown.Item onClick={() => onAddItem(FormFieldType.Boolean, list)}>Boolean</Dropdown.Item>
                    <Dropdown.Item onClick={() => onAddItem(FormFieldType.Date, list)}>Date</Dropdown.Item>
                    <Dropdown.Item onClick={() => onAddItem(FormFieldType.DateTime, list)}>Date/Time</Dropdown.Item>
                </>
            );
        }

        return menuItems;
    }, [onAddItem]);

    if (field.type === FormFieldType.Boolean) {
        control = (
            <Toggle
                // required={field.isRequired as boolean}
                checked={value?.boolean ?? false}
                onChange={event => onChange?.({ ...value, type: FormFieldType.Boolean, boolean: event.target.checked })}
            />
        );
    } else if (field.type === FormFieldType.Json) {
        const schema = (field as WorkflowFormField).schema as WorkflowFormFieldSchema;
        isFullWidth = true;
        const jsonValue = value?.string ? JSON.parse(value.string) as WorkflowStepKeyValueParameterInput[] : undefined;
        const fieldMap = isRecord(schema.root) ? schema.root.fields.toIdMap("id") : {};
        const areWorkflows = Object.values(fieldMap).some(row => row.type === FormFieldType.Workflow
            || row.type === FormFieldType.WorkflowTicket);
        const valueList = jsonValue?.mapFilter(row => {
            const fieldValue = fieldMap[row.key];
            return fieldValue ? {
                ...row,
                id: row.key,
                field: fieldValue,
            } : undefined;
        }) ?? [];
        //
        control = (
            <TableContainer data={valueList ?? []}>
                <TableContainer.TableControls>
                    <TableContainer.TableControls.Search
                        autoFocus={false}
                        search={(query, item) => item.id?.toLocaleString().includes(query)}
                    />
                    <TableContainer.TableControls.Seperator />
                    <TableContainer.TableControls.Filters shouldWriteUrl={false} />
                    <TableContainer.TableControls.SortBy />
                    <Dropdown>
                        <Dropdown.Toggle variant="primary" size="small" prefixIcon={<RiAddLine />}>
                            Add
                            {" "}
                            {schema?.label}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {createMenuItems(jsonValue, schema)}
                        </Dropdown.Menu>
                    </Dropdown>
                </TableContainer.TableControls>
                <TableContainer.Table>

                    <TableContainer.TableValue
                        columnKey="name"
                        title="Name"
                        sortBy={item => item.field.name}
                    >
                        {item => item.field.name as string}
                    </TableContainer.TableValue>
                    <TableContainer.TableValue
                        columnKey="description"
                        title="Description"
                        sortBy={item => item.field.description}
                    >
                        {item => (item.field.description
                            ? <HtmlRenderer html={item.field.description} shouldTrimBottom />
                            : "")}
                    </TableContainer.TableValue>

                    {areWorkflows && (
                        <TableContainer.TableValue
                            columnKey="workflow"
                            title="Workflow"
                        >
                            {item => (
                                <Button.Automation
                                    size="small"
                                    variant="ghost-blue"
                                    onClick={() => {
                                        onOpenWorkflow(item);
                                    }}
                                >
                                    Edit Workflow
                                </Button.Automation>
                            )}
                        </TableContainer.TableValue>
                    )}
                </TableContainer.Table>
            </TableContainer>
        );
    } else if (field.type === FormFieldType.LongText) {
        control = (
            <FormGroup
                type="text"
                display="textarea"
                size="small"
                isRequired={field.isRequired as boolean}
                value={value?.string ?? ""}
                onChange={event => onChange?.({ ...value, type: FormFieldType.LongText, string: event.target.value })}
            />
        );
    }

    if (!control) {
        control = (
            <FieldTypeValueDisplay
                className={field.type === FormFieldType.Dropdown || field.type === FormFieldType.AsyncDropdown
                    ? SettingsContainer.Setting.PickerClassName
                    : undefined}
                inputClassName="form-control"
                shouldShowFieldName={false}
                shouldShowDescription={false}
                field={{
                    ...field,
                    isRequired: false,
                }}
                value={value}
                integrationId={integration?.id}
                getCurrentForm={() => optionValues}
                onChange={onChange}
            />
        );
    }

    return (
        <SettingsContainer.Setting
            title={field.name ?? ""}
            description={description ?? ""}
            descriptionElement={description ? <HtmlRenderer html={description} /> : undefined}
            permissions={SettingsContainer.Setting.Permissions.Administrator}
            isHideTitle={isFullWidth}
        >
            {control}
        </SettingsContainer.Setting>
    );
}

export default function ViewServiceSettings({
    id,
    childId,
}: {
    id: string;
    childId: string
}) {
    const { data, loading: isLoading } = useGetWorkflowServiceQuery({
        variables: {
            id,
        },
    });

    const [viewWorkflowId, setViewWorkflowId] = useState<string | undefined>(undefined);
    const [viewWorkflowInput, setViewWorkflowInput] = useState<ReplacementGroup[]>([]);

    const integration = useMemo(() => data?.workflowService?.installedIntegrations
        ?.find(current => current.id === childId), [data?.workflowService?.installedIntegrations, childId]);

    const { historyPush, Paths } = useRouting();

    const { properties, tracker } = useFormBuilder(
        builder => builder
            .property("label", () => integration?.label ?? "")
            .array("options", () => integration?.settings?.map(copySetting) ?? [])
            .array("permissions", () => integration?.actionPermissions),
        [integration?.settings],
    );
    const [save] = useSaveIntegrationSettingsMutation();
    const [uninstall] = useUninstallWorkflowServiceMutation();

    const { element: saveElement } = tracker.useDirtyTracker({
        finishedSavingMessage: `Saved ${integration?.label} settings.`,
        onSave: async (__, onDone) => {
            await save({
                refetchQueries: [
                    { query: GetWorkflowServiceDocument, variables: { id } },
                ],
                variables: {
                    settings: {
                        id: integration?.id,
                        settings: cloneWithoutTypename(properties.options.value),
                        label: properties.label.value,
                        actionPermissions: properties.permissions.value
                            ? cloneWithoutTypename(properties.permissions.value)
                            : undefined,
                    },
                },
            });
            onDone();
        },
    });

    const { element: deleteElement, setDeleting } = useDeleteConfirm<Integration>(
        () => {
            LoggingHandler.instance.wrap(async () => {
                if (!integration) {
                    return;
                }
                await uninstall({
                    refetchQueries: [
                        { query: GetWorkflowServiceDocument, variables: { id } },
                        { query: GetWorkflowServicesDocument },
                    ],
                    variables: {
                        integrationId: integration.id,
                    },
                });

                historyPush(Paths.ADMINISTRATOR_SETTING, { setting: "workflow-services" });
            });
        },
        value => value.label,
        undefined,
        "Are you sure you want to uninstall",
        "Uninstall",

    );

    useSetTitle(integration?.label, [integration]);

    const valuesMap = properties.options.value?.toIdMap("fieldId") ?? {};

    const handleChangeSettingInput = useCallback((changedValue: FieldTypedValue, field: FormField) => {
        const copy = [...properties.options.value];
        const index = properties.options.value.findIndex(row => row.fieldId === field.id);
        if (index >= 0) {
            copy[index] = changedValue;
        } else {
            copy.push({
                ...changedValue,
                fieldId: field.id as string,
                type: field.type as FormFieldType,
            });
        }

        properties.options.setValue(copy);
    }, [properties.options.value]);

    const handleOpenWorkflow = useCallback((item: WorkflowStepKeyValueParameterInputWithId) => {
        if (item.value.string) {
            setViewWorkflowId(item.value.string);
            const schema = item.field?.schema as WorkflowFormFieldSchema;
            const input = {
                id: "urn:fq:form-field-group:execute-context",
                label: schema?.label as string,
                description: schema?.description as string,
                input: isRecord(schema?.root)
                    ? schema?.root.fields.flatMap(fieldOption => (
                        fieldOption.type === FormFieldType.User
                            ? createUserWorkflowFields(fieldOption)
                            : fieldOption
                    )) as FormField[]
                    : undefined,
            };

            setViewWorkflowInput([input]);
        }
    }, []);

    const serviceOptions = useMemo(() => integration && data?.workflowService?.options.map(section => (
        <SettingsContainer.Section
            title={section.title}
            helpText={section.description ?? ""}
        >

            {section.options.map(option => {
                const field = option.input as FormField;
                const value = valuesMap[field?.id] ?? field?.defaultValue;
                if (!field) {
                    return null;
                }
                return (
                    <SettingInput
                        field={field}
                        value={value}
                        key={field.id}
                        option={option as WorkflowServiceOption}
                        optionValues={properties.options.value}
                        integration={integration as Integration}
                        onChange={changedValue => handleChangeSettingInput(changedValue, field)}
                        onOpenWorkflow={handleOpenWorkflow}
                    />

                );
            })}
        </SettingsContainer.Section>
    )), [data?.workflowService?.options]);

    return (
        <SettingsContainer saveElement={saveElement}>
            {deleteElement}

            <SettingsContainer.Section
                title="General Info"
            >
                <SettingsContainer.Setting
                    title="Connection Name"
                    description="Set up custom connection name."
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isLoading={isLoading}
                >
                    <FormGroup
                        type="text"
                        size="small"
                        value={properties.label.value}
                        onChange={properties.label.setValueFromInput}
                    />

                </SettingsContainer.Setting>
                <SettingsContainer.Setting
                    title="Linked Account"
                    description="Integration account that is linked to Foqal."
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isLoading={isLoading}
                >
                    <FormGroup
                        size="small"
                        type="text"
                        isDisabled
                        value={integration?.connectionUrl ? new URL(integration?.connectionUrl).hostname : ""}
                    />

                </SettingsContainer.Setting>
            </SettingsContainer.Section>

            {serviceOptions}
            {
                viewWorkflowId && (
                    <ViewAutomation
                        workflowId={viewWorkflowId}
                        onHide={() => {
                            setViewWorkflowId(undefined);
                        }}
                        startTab={AutomationTab.AUTOMATION}
                        form={{
                            formId: undefined,
                            formType: WorkflowTriggerType.WorkspaceAction,
                        }}
                        input={viewWorkflowInput}
                        hideGeneralDetailsTab
                    />
                )
            }
            <SettingsContainer.Section
                title="Permissions"
            >
                {data?.workflowService?.actionTypes?.sortBy(row => row).map(type => {
                    if (!PERMISSION_GROUPS[type]) {
                        return null;
                    }
                    const { text, description } = PERMISSION_GROUPS[type];
                    const permission = properties.permissions.value?.find(group => group.action === type);
                    return (
                        <SettingsContainer.Setting
                            title={text}
                            key={type}
                            description={description}
                            permissions={SettingsContainer.Setting.Permissions.Administrator}
                        >

                            <UserGroups
                                isMulti
                                isClearable
                                canSelectEveryone
                                canSelectNone
                                className={SettingsContainer.Setting.PickerClassName}
                                value={permission?.groupIds ?? [EVERYONE_GROUP_ID]}
                                onSelected={groupIds => {
                                    const copy = [
                                        ...(properties.permissions.value ?? []),
                                    ];
                                    const currentIndex = copy?.findIndex(group => group.action === type);
                                    if (currentIndex >= 0) {
                                        copy[currentIndex] = {
                                            ...copy[currentIndex],
                                            groupIds: groupIds as string[],
                                        };
                                    } else {
                                        copy.push({
                                            action: type,
                                            groupIds: groupIds as string[],
                                        });
                                    }
                                    properties.permissions.setValue(copy);
                                }}

                            />

                        </SettingsContainer.Setting>
                    );
                })}
            </SettingsContainer.Section>
            <SettingsContainer.Section
                title="Danger Zone"
            >
                <SettingsContainer.Setting
                    title="Uninstall Connection"
                    description="This will deactivate the connection from interacting with this space."
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                >

                    <Button variant="outline-danger" onClick={() => setDeleting(integration)} prefixIcon={<RiLinkUnlinkM />}>
                        Uninstall
                    </Button>

                </SettingsContainer.Setting>
            </SettingsContainer.Section>

        </SettingsContainer>
    );
}
